import _ from 'lodash';

const TOKEN_REGEX = /\{(\w+)\}/g;

const isEmpty = value => value === null || value === undefined || value === '';

/**
 * Resolve single-brace `{token}` placeholders inside an iframe URL.
 *
 * - Tokens located in the origin / path are replaced with the resolved value,
 *   or an empty string when the value is missing.
 * - Tokens located inside a query-string parameter value cause the ENTIRE
 *   `key=value` pair to be dropped when the referenced value is missing/empty
 *   (so we never emit `x=` or `x=undefined`).
 *
 * Pure function — no side effects. Shared so DEV-3008 can reuse it.
 *
 * @param {string} rawUrl - URL template, e.g. `https://x.com/a?loan={loanNumber}`.
 * @param {Object} [context] - Token values, e.g. `{ applicationId, loanNumber, loanId, companyId }`.
 * @returns {string} The resolved URL with missing query params omitted.
 */
export const resolveIframeUrl = (rawUrl, context = {}) => {
  if (!rawUrl || !_.isString(rawUrl)) {
    return '';
  }

  const queryIndex = rawUrl.indexOf('?');
  const hasQuery = queryIndex !== -1;
  const base = hasQuery ? rawUrl.slice(0, queryIndex) : rawUrl;
  const queryString = hasQuery ? rawUrl.slice(queryIndex + 1) : '';

  // Tokens in the origin/path: missing -> empty string.
  const resolvedBase = base.replace(TOKEN_REGEX, (match, token) => {
    const value = context[token];

    return isEmpty(value) ? '' : String(value);
  });

  if (!hasQuery) {
    return resolvedBase;
  }

  // Tokens in the query: a param is dropped entirely if any referenced token is missing.
  const resolvedParams = queryString
    .split('&')
    .filter(Boolean)
    .reduce((acc, pair) => {
      const eqIndex = pair.indexOf('=');
      const key = eqIndex === -1 ? pair : pair.slice(0, eqIndex);
      const value = eqIndex === -1 ? '' : pair.slice(eqIndex + 1);

      const tokensInValue = [...value.matchAll(TOKEN_REGEX)].map(([, token]) => token);
      const hasMissingToken = tokensInValue.some(token => isEmpty(context[token]));

      if (hasMissingToken) {
        return acc;
      }

      const resolvedValue = value.replace(TOKEN_REGEX, (match, token) => String(context[token]));

      acc.push(eqIndex === -1 ? key : `${key}=${resolvedValue}`);

      return acc;
    }, []);

  return resolvedParams.length ? `${resolvedBase}?${resolvedParams.join('&')}` : resolvedBase;
};
